import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AsyncTaskStatus} from "../../shared/dtos";
import {AppLogDTO, AppUserLookupDTO, MailLogDTO, PaginatedEntityList, UserDTO} from "../../api/dtos";

export interface AppUserState {
    loadAllUsersLookupTaskStatus: AsyncTaskStatus | null;
    loadNonCompanyUsersLookupTaskStatus: AsyncTaskStatus | null;
    createUserTaskStatus: AsyncTaskStatus | null;
    resetPasswordTaskStatus: AsyncTaskStatus | null;
    resetOwnPasswordTaskStatus: AsyncTaskStatus | null;
    editUserTaskStatus: AsyncTaskStatus | null;
    allPaginatedUsers: PaginatedEntityList<UserDTO> | null;
    allUsersLookup: AppUserLookupDTO[] | null;
    nonCompanyUsersLookup: AppUserLookupDTO[] | null;
    editUserPermissionsTaskStatus: AsyncTaskStatus | null;
    getLogsTaskStatus: AsyncTaskStatus | null;
    logs: PaginatedEntityList<AppLogDTO> | null;
    getMailLogsTaskStatus: AsyncTaskStatus | null;
    mailLogs: PaginatedEntityList<MailLogDTO> | null;
}

const initialState: AppUserState = {
    createUserTaskStatus: null,
    loadAllUsersLookupTaskStatus: null,
    nonCompanyUsersLookup: null,
    loadNonCompanyUsersLookupTaskStatus: null,
    editUserTaskStatus: null,
    resetPasswordTaskStatus: null,
    resetOwnPasswordTaskStatus: null,
    allPaginatedUsers: null,
    allUsersLookup: null,
    editUserPermissionsTaskStatus: null,
    getLogsTaskStatus: null,
    logs: null,
    getMailLogsTaskStatus: null,
    mailLogs: null
};

const appUserSlice = createSlice({
    name: 'appUser',
    initialState,
    reducers: {
        setAllUsersLookup: (state, action: PayloadAction<AppUserLookupDTO[] | null>) => {
            state.allUsersLookup = action.payload;
        },
        setCreateUserTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.createUserTaskStatus = action.payload;
        },
        setGetAllUsersLookupTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.loadAllUsersLookupTaskStatus = action.payload;
        },
        setNonCompanyUsersLookup: (state, action: PayloadAction<AppUserLookupDTO[] | null>) => {
            state.nonCompanyUsersLookup = action.payload;
        },
        setGetNonCompanyUsersLookupTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.loadNonCompanyUsersLookupTaskStatus = action.payload;
        },
        setAllPaginatedUsers: (state, action: PayloadAction<PaginatedEntityList<UserDTO> | null>) => {
            state.allPaginatedUsers = action.payload;
        },
        setResetPasswordTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.resetPasswordTaskStatus = action.payload;
        },
        setResetOwnPasswordTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.resetOwnPasswordTaskStatus = action.payload;
        },
        setEditUserTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.editUserTaskStatus = action.payload;
        },
        setEditUserPermissionsTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.editUserPermissionsTaskStatus = action.payload;
        },
        setGetLogsTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.getLogsTaskStatus = action.payload;
        },
        setLogs: (state, action: PayloadAction<PaginatedEntityList<AppLogDTO> | null>) => {
            state.logs = action.payload;
        },
        setGetMailLogsTaskStatus: (state, action: PayloadAction<AsyncTaskStatus | null>) => {
            state.getMailLogsTaskStatus = action.payload;
        },
        setMailLogs: (state, action: PayloadAction<PaginatedEntityList<MailLogDTO> | null>) => {
            state.mailLogs = action.payload;
        }
    }
});

export const {
    setCreateUserTaskStatus,
    setGetAllUsersLookupTaskStatus,
    setGetNonCompanyUsersLookupTaskStatus,
    setAllUsersLookup,
    setNonCompanyUsersLookup,
    setAllPaginatedUsers,
    setEditUserTaskStatus,
    setResetPasswordTaskStatus,
    setResetOwnPasswordTaskStatus,
    setEditUserPermissionsTaskStatus,
    setGetLogsTaskStatus,
    setLogs,
    setGetMailLogsTaskStatus,
    setMailLogs
} = appUserSlice.actions;

export default appUserSlice.reducer;
